// IMPORT (HARDEN): guided imports of another tool's export — Setup › Integrations › Import
// (services/api/src/routes/import-runs.ts, imports/runs/*). Owners only. The owner uploads a
// Zulip realm export (a `.tar` / `.tar.gz` of `realm.json` + `messages-*.json`) or a ClickUp CSV
// export; the API stores the file and answers what it holds (the preview) with a first mapping
// (each Zulip stream → a channel, each ClickUp space → a space and list → a board; each person →
// a company member matched by email, else skipped). The owner edits the mapping, asks for a dry
// run (counts, conflicts, warnings; nothing written), starts it (a background job, progress on
// the run) and reads the report. Re-running an import of the same export writes nothing twice.
import { z } from "zod";
import { ImportRunSource, ImportRunStatus } from "../enums";
import { boundedRecord, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** The largest export the upload takes (the app-wide body cap is raised for this one path). */
export const IMPORT_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

const Key = z.string().min(1).max(300);
/** Containers or people one mapping may name (an export's streams, lists or senders). */
const MAPPING_KEYS = 5_000;
const Count = z.int().min(0);
/** Counts by what was (or would be) written: `spaces`, `channels`, `messages`, `tasks`, … */
const Tally = z.record(z.string(), Count);

/** What a container of the export becomes here. */
export const ImportContainerKind = z.enum(["space", "channel", "board"]);

export const ImportPreviewContainer = z
  .object({
    /** Stable across uploads of the same export: `zulip:<stream id>`, `clickup:list:<name>`, … */
    key: Key,
    kind: ImportContainerKind,
    name: z.string(),
    /** The space a channel or board belongs to (a key of this preview), null for a space. */
    parent_key: Key.nullable(),
    is_private: z.boolean(),
    /** Messages (a stream) or tasks (a list, or every list of a space) it holds. */
    items: Count,
    /** Topics of a Zulip stream; 0 otherwise. */
    topics: Count,
  })
  .meta({ id: "ImportPreviewContainer" });

export const ImportPreviewPerson = z
  .object({
    /** `zulip:user:<id>` or `clickup:person:<lower-cased name>`. */
    key: Key,
    name: z.string(),
    email: z.string().nullable(),
    /** Messages they sent or tasks assigned to them. */
    items: Count,
    /** The company's person with this email (or, for ClickUp, this name), when exactly one. */
    match: z.object({ membership_id: Uuid, display_name: z.string() }).nullable(),
  })
  .meta({ id: "ImportPreviewPerson" });

export const ImportPreview = z
  .object({
    source: ImportRunSource,
    containers: z.array(ImportPreviewContainer),
    people: z.array(ImportPreviewPerson),
    counts: Tally,
    /** Zulip: the 12 months of stream messages imported (older history is only counted). */
    window: z.object({ from: Timestamp, to: Timestamp }).nullable(),
    warnings: z.array(z.string()),
  })
  .meta({ id: "ImportPreview" });

export const ImportContainerTarget = z
  .discriminatedUnion("action", [
    /** A new space / channel / board with this name (an existing live one of that name is reused). */
    z.object({ action: z.literal("create"), name: z.string().trim().min(1).max(200) }),
    /** An existing live space / channel / board of the company. */
    z.object({ action: z.literal("existing"), id: Uuid }),
    z.object({ action: z.literal("skip") }),
  ])
  .meta({ id: "ImportContainerTarget" });

export const ImportPersonTarget = z
  .discriminatedUnion("action", [
    /** An active person of the company: their messages and tasks are theirs. */
    z.object({ action: z.literal("match"), membership_id: Uuid }),
    /** Invite their email as a member (Zulip people with an email only). */
    z.object({ action: z.literal("invite") }),
    /** Kept as a name in the text ("[Name · date]"), owned by no one. */
    z.object({ action: z.literal("skip") }),
  ])
  .meta({ id: "ImportPersonTarget" });

export const ImportMapping = z
  .object({
    /** Preview container key → its target; every key of the preview, nothing else. */
    containers: boundedRecord(ImportContainerTarget, MAPPING_KEYS, Key),
    /** Preview person key → its target; every key of the preview, nothing else. */
    people: boundedRecord(ImportPersonTarget, MAPPING_KEYS, Key),
  })
  .meta({ id: "ImportMapping" });

export const ImportIssue = z
  .object({ key: Key.nullable(), code: z.string(), message: z.string() })
  .meta({ id: "ImportIssue" });

export const ImportDryRun = z
  .object({
    /** No conflict: the run may start. */
    ready: z.boolean(),
    created: Tally,
    /** Already here (a re-run, an existing target): kept, not written again. */
    reused: Tally,
    skipped: Tally,
    conflicts: z.array(ImportIssue),
    warnings: z.array(ImportIssue),
  })
  .meta({ id: "ImportDryRun" });

export const ImportReport = z
  .object({
    created: Tally,
    /** Already here and kept (reused spaces, channels, boards; messages or tasks of a re-run). */
    updated: Tally,
    skipped: Tally,
    warnings: z.array(ImportIssue),
    errors: z.array(ImportIssue),
    started_at: Timestamp,
    finished_at: Timestamp,
  })
  .meta({ id: "ImportReport" });

export const ImportRunSummary = z
  .object({
    id: Uuid,
    company_id: Uuid,
    source: ImportRunSource,
    status: ImportRunStatus,
    file_name: z.string(),
    file_bytes: Count,
    progress: z.object({ phase: z.string().nullable(), done: Count, total: Count }),
    error: z.string().nullable(),
    cancel_requested: z.boolean(),
    created_by_membership_id: Uuid.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
    queued_at: Timestamp.nullable(),
    started_at: Timestamp.nullable(),
    finished_at: Timestamp.nullable(),
  })
  .meta({ id: "ImportRunSummary" });

export const ImportRun = ImportRunSummary.extend({
  preview: ImportPreview,
  mapping: ImportMapping,
  report: ImportReport.nullable(),
}).meta({ id: "ImportRun" });

export const ImportRunUpload = z
  .object({
    source: ImportRunSource.describe("multipart part `source`"),
    file: z
      .string()
      .describe(
        "the export (multipart part `file`): Zulip `.tar` / `.tar.gz` of realm.json + messages-*.json, or ClickUp's CSV export",
      )
      .meta({ format: "binary" }),
  })
  .meta({ id: "ImportRunUpload" });

const item = `${base}/import-runs/{id}`;

export const importRunRoutes = {
  "importRuns.list": defineRoute({
    ...core,
    id: "importRuns.list",
    method: "get",
    path: `${base}/import-runs`,
    tag: "imports",
    summary: "Import runs (Zulip, ClickUp), newest first. Owners",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(ImportRunSummary) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "importRuns.create": defineRoute({
    ...core,
    id: "importRuns.create",
    method: "post",
    path: `${base}/import-runs`,
    tag: "imports",
    summary:
      "Upload a Zulip or ClickUp export: stored, previewed, with a first mapping (Zulip needs the chat add-on). Owners",
    access: "manager",
    params: CompanyParams,
    body: ImportRunUpload,
    bodyType: "multipart",
    response: { status: 201, schema: ImportRun },
    errors: ["payload_too_large", "feature_disabled", "feature_read_only"],
    emits: ["import_run.created"],
  }),
  "importRuns.get": defineRoute({
    ...core,
    id: "importRuns.get",
    method: "get",
    path: item,
    tag: "imports",
    summary: "One import run: preview, mapping, progress and report. Owners",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: ImportRun },
    errors: [],
    emits: [],
  }),
  "importRuns.updateMapping": defineRoute({
    ...core,
    id: "importRuns.updateMapping",
    method: "put",
    path: `${item}/mapping`,
    tag: "imports",
    summary: "Replace the mapping of an import that has not started. Owners",
    access: "manager",
    params: CompanyItemParams,
    body: ImportMapping,
    response: { status: 200, schema: ImportRun },
    errors: ["conflict", "feature_disabled", "feature_read_only"],
    emits: ["import_run.mapping_updated"],
  }),
  "importRuns.dryRun": defineRoute({
    ...core,
    id: "importRuns.dryRun",
    method: "post",
    path: `${item}/dry-run`,
    tag: "imports",
    summary:
      "What the import would write with its mapping: counts, conflicts, warnings. Writes nothing",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: ImportDryRun },
    errors: ["feature_disabled"],
    emits: [],
    safe: true,
  }),
  "importRuns.start": defineRoute({
    ...core,
    id: "importRuns.start",
    method: "post",
    path: `${item}/start`,
    tag: "imports",
    summary:
      "Queue the import for the background job (refused with conflicts, or while another runs). Owners",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 202, schema: ImportRun },
    errors: ["conflict", "feature_disabled", "feature_read_only"],
    emits: ["import_run.queued"],
  }),
  "importRuns.cancel": defineRoute({
    ...core,
    id: "importRuns.cancel",
    method: "post",
    path: `${item}/cancel`,
    tag: "imports",
    summary:
      "Cancel an import: at once when it has not started, at the next batch when it is running. Owners",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: ImportRun },
    errors: ["conflict"],
    emits: ["import_run.cancelled", "import_run.cancel_requested"],
  }),
} as const;
